import type { BriefAccessSavedType } from 'api-client/briefs';
import { defineMessages } from 'react-intl';
import { accountDisplayName } from '../AccountDisplayName';
import type { BriefAccessRoleType, PrincipalType } from 'bc-model';

const accessPrincipalDisplayName = (accessRecord: BriefAccessSavedType): string => {
  const { account, group, organization } = accessRecord;
  if (account) {
    return accountDisplayName(account);
  }
  if (group) {
    return group.name;
  }
  if (organization) {
    return organization.name;
  }
  return '';
};

const getPrincipalType = (access: BriefAccessSavedType): PrincipalType => {
  if (access.account) {
    return 'user';
  }
  if (access.group) {
    return 'group';
  }
  return 'organization';
};

const userPermissionOptions = [
  'reader',
  'commenter',
  'editor',
  'admin',
] as const satisfies BriefAccessRoleType[];

const roleAccessLabelMessages = defineMessages({
  fullAccess: {
    id: 'PlanningTeam.access.fullAccess',
    defaultMessage: 'Admin',
  },
  canEdit: {
    id: 'PlanningTeam.access.canEdit',
    defaultMessage: 'Editor',
  },
  canComment: {
    id: 'PlanningTeam.access.canComment',
    defaultMessage: 'Commenter',
  },
  canView: {
    id: 'PlanningTeam.access.canView',
    defaultMessage: 'Reader',
  },
  noAccess: {
    id: 'PlanningTeam.access.noAccess',
    defaultMessage: 'No access',
  },
});

// Order for sorting by access label (Admin → Editor → Commenter → Reader)
const ACCESS_LABEL_SORT_ORDER: (keyof typeof roleAccessLabelMessages)[] = [
  'fullAccess',
  'canEdit',
  'canComment',
  'canView',
];

const ROLE_TO_ACCESS_LABEL_MESSAGE: Record<
  BriefAccessRoleType,
  keyof typeof roleAccessLabelMessages
> = {
  owner: 'fullAccess',
  admin: 'fullAccess',
  editor: 'canEdit',
  commenter: 'canComment',
  reader: 'canView',
};

function getAccessLabelOrder(role: BriefAccessRoleType): number {
  const label = ROLE_TO_ACCESS_LABEL_MESSAGE[role];
  const index = ACCESS_LABEL_SORT_ORDER.indexOf(label);
  return index === -1 ? ACCESS_LABEL_SORT_ORDER.length : index;
}

/** Sort by the chosen column; name/type alphabetically, access by displayed label (Admin, Editor, Reader). */
function sortPlanningTeamMembers(
  accessList: BriefAccessSavedType[],
  sortBy: PlanningTeamSortBy,
  direction: SortDirection
): BriefAccessSavedType[] {
  const sign = direction === 'asc' ? 1 : -1;
  return [...accessList].sort((a, b) => {
    if (sortBy === 'name') {
      return (
        sign *
        accessPrincipalDisplayName(a).localeCompare(accessPrincipalDisplayName(b), undefined, {
          sensitivity: 'base',
        })
      );
    }
    if (sortBy === 'type') {
      return sign * getPrincipalType(a).localeCompare(getPrincipalType(b));
    }
    const orderA = getAccessLabelOrder(a.role);
    const orderB = getAccessLabelOrder(b.role);
    if (orderA !== orderB) return sign * (orderA - orderB);
    return sign * a.role.localeCompare(b.role);
  });
}

export type PlanningTeamSortBy = 'access' | 'name' | 'type';
export type SortDirection = 'asc' | 'desc';
export {
  accessPrincipalDisplayName,
  getPrincipalType,
  sortPlanningTeamMembers,
  userPermissionOptions,
};

export { roleAccessLabelMessages, ROLE_TO_ACCESS_LABEL_MESSAGE };

/**
 * Determines if the user with the given accountId can leave a plan.
 * Owners cannot leave; admins can leave only if another owner or admin (with a direct accountId) remains.
 * Editors, commenters, and readers can always leave.
 */
export const canLeavePlan = (accountId: number | undefined, accessRecords: BriefAccessSavedType[]): boolean => {
  if (!accountId) return false;
  const currentAccessRecord = accessRecords.find((record) => record.accountId === accountId);
  if (!currentAccessRecord) return false;
  if (currentAccessRecord.role === 'owner') return false;
  if (['editor', 'commenter', 'reader'].includes(currentAccessRecord.role)) return true;
  return accessRecords.some(
    (r) => r.accountId && ['owner', 'admin'].includes(r.role) && r.accountId !== accountId
  );
};
